import React from "react";
import hosys from "@/api/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, FileText, Users, Clock, Scale } from "lucide-react";

const LEGAL_BASIS_SHORT = {
  consent: "Consentiment",
  contract: "Contracte",
  legal_obligation: "Obligació legal",
  vital_interest: "Interès vital",
  public_task: "Tasca pública",
  legitimate_interest: "Interès legítim",
};

const LEGAL_BASIS_COLOR = {
  consent: "bg-blue-100 text-blue-700",
  contract: "bg-emerald-100 text-emerald-700",
  legal_obligation: "bg-amber-100 text-amber-700",
  vital_interest: "bg-red-100 text-red-700",
  public_task: "bg-purple-100 text-purple-700",
  legitimate_interest: "bg-gray-100 text-gray-600",
};

export default function ProcessingActivitiesList({ items, onEdit, onDeleted, isAdmin }) {
  const handleDelete = async (id) => {
    await hosys.entities.DataProcessingActivity.delete(id);
    onDeleted(id);
  };

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <FileText className="w-10 h-10 mx-auto mb-2 text-gray-200" />
        <p className="text-sm">Sense activitats de tractament registrades</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map(item => (
        <Card key={item.id} className="p-4 border-0 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <h4 className="font-semibold text-gray-900 text-sm">{item.name}</h4>
                <Badge className={`${LEGAL_BASIS_COLOR[item.legal_basis] || "bg-gray-100 text-gray-600"} border-0 text-[10px]`}>
                  {LEGAL_BASIS_SHORT[item.legal_basis]}
                </Badge>
              </div>
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.purpose}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5">
                {(item.data_categories || []).length > 0 && (
                  <span className="text-[11px] text-gray-400 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {item.data_categories.slice(0, 3).join(", ")}{item.data_categories.length > 3 ? ` +${item.data_categories.length - 3}` : ""}
                  </span>
                )}
                {(item.data_subjects || []).length > 0 && (
                  <span className="text-[11px] text-gray-400 flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {item.data_subjects.join(", ")}
                  </span>
                )}
                {item.retention_period && (
                  <span className="text-[11px] text-gray-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {item.retention_period}
                  </span>
                )}
              </div>
            </div>
            {isAdmin && (
              <div className="flex gap-1 flex-shrink-0">
                <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-[#7B2D3F]" onClick={() => onEdit(item)}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-red-600" onClick={() => handleDelete(item.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}